import { VIEWPORT_PADDING_RATIO } from './constants';

// Long ELK/orthogonal path strings are expensive to re-parse on every cull pass; same pathD is stable.
const PATH_BBOX_CACHE = new Map();
const PATH_BBOX_CACHE_MAX = 400;

/**
 * Extract a bounding box from an SVG path string.
 * Parses M, L, Q, C commands to find all coordinate points, then returns
 * the min/max extents. This is a lightweight approximation — for quadratic
 * and cubic curves it uses control points (which always contain the curve),
 * so the box may be slightly larger than the true curve, which is fine for
 * visibility culling.
 *
 * @param {string} pathD - SVG path d attribute
 * @returns {{ minX: number, minY: number, maxX: number, maxY: number } | null}
 */
export function getPathBoundingBox(pathD) {
  if (!pathD) return null;

  const cached = PATH_BBOX_CACHE.get(pathD);
  if (cached !== undefined) {
    return cached;
  }

  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  let hasPoints = false;

  // Match all numeric coordinate pairs in the path
  // This catches coordinates from M, L, Q, C, and other commands
  const numRegex = /[-+]?(?:\d+\.?\d*|\.\d+)(?:[eE][-+]?\d+)?/g;
  const numbers = [];
  let match;
  while ((match = numRegex.exec(pathD)) !== null) {
    numbers.push(parseFloat(match[0]));
  }

  // Process pairs of numbers as (x, y) coordinates
  for (let i = 0; i < numbers.length - 1; i += 2) {
    const x = numbers[i];
    const y = numbers[i + 1];
    if (isFinite(x) && isFinite(y)) {
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
      hasPoints = true;
    }
  }

  const box = hasPoints ? { minX, minY, maxX, maxY } : null;
  if (PATH_BBOX_CACHE.size >= PATH_BBOX_CACHE_MAX) {
    PATH_BBOX_CACHE.clear();
  }
  PATH_BBOX_CACHE.set(pathD, box);
  return box;
}

/** Check if two axis-aligned bounding boxes overlap */
export function boxesOverlap(a, b) {
  return !(a.maxX < b.minX || a.minX > b.maxX || a.maxY < b.minY || a.minY > b.maxY);
}

/**
 * True when the box spanning both edge endpoints overlaps the viewport.
 * Missing positions stay visible so a wire is not dropped before it has geometry.
 */
export function endpointBoxTouchesBounds(edge, bounds) {
  if (!bounds) return true;
  const a = edge?.fromPos;
  const b = edge?.toPos;
  if (!a || !b) return true;
  const fw = edge.fromDims?.width || 0;
  const fh = edge.fromDims?.height || 0;
  const tw = edge.toDims?.width || 0;
  const th = edge.toDims?.height || 0;
  return boxesOverlap(
    {
      minX: Math.min(a.x, b.x),
      minY: Math.min(a.y, b.y),
      maxX: Math.max(a.x + fw, b.x + tw),
      maxY: Math.max(a.y + fh, b.y + th),
    },
    bounds,
  );
}

export function visibleSetsEqual(a, b) {
  if (a.size !== b.size) return false;
  for (const id of a) {
    if (!b.has(id)) return false;
  }
  return true;
}

/**
 * Calculate the visible bounds in canvas coordinates
 * @param {Object} containerRect - Container bounding rect { width, height }
 * @param {Object} pan - Current pan { x, y }
 * @param {number} zoom - Current zoom level
 * @param {number} [paddingRatio] - multiples of viewport width/height as padding (default VIEWPORT_PADDING_RATIO)
 * @returns {Object} Visible bounds { minX, minY, maxX, maxY } in canvas coordinates
 */
export function calculateVisibleBounds(containerRect, pan, zoom, paddingRatio = VIEWPORT_PADDING_RATIO) {
  if (!containerRect || !containerRect.width || !containerRect.height) {
    return null;
  }

  const { width: viewportWidth, height: viewportHeight } = containerRect;

  // Calculate padding in screen pixels, then convert to canvas coordinates
  const paddingX = viewportWidth * paddingRatio;
  const paddingY = viewportHeight * paddingRatio;

  // Convert screen coordinates to canvas coordinates
  // Screen point (0, 0) in canvas coords: (-pan.x) / zoom
  // Screen point (width, height) in canvas coords: (width - pan.x) / zoom
  const screenMinX = -paddingX;
  const screenMinY = -paddingY;
  const screenMaxX = viewportWidth + paddingX;
  const screenMaxY = viewportHeight + paddingY;

  return {
    minX: (screenMinX - pan.x) / zoom,
    minY: (screenMinY - pan.y) / zoom,
    maxX: (screenMaxX - pan.x) / zoom,
    maxY: (screenMaxY - pan.y) / zoom,
  };
}

/**
 * Check if a node is within the visible bounds
 * @param {Object} position - Node position { x, y }
 * @param {Object} dimensions - Node dimensions { width, height }
 * @param {Object} bounds - Visible bounds { minX, minY, maxX, maxY }
 * @returns {boolean} True if node is visible
 */
export function isNodeInBounds(position, dimensions, bounds) {
  if (!bounds) return true; // No bounds = render all

  const nodeRight = position.x + dimensions.width;
  const nodeBottom = position.y + dimensions.height;

  // Node is visible if it overlaps with the visible bounds
  return !(
    nodeRight < bounds.minX ||
    position.x > bounds.maxX ||
    nodeBottom < bounds.minY ||
    position.y > bounds.maxY
  );
}
